import React from 'react';
import { Package, MapPin, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import StatusTimeline from './StatusTimeline';

const TrackingResult = ({ data, trackingNumber }) => {
  const getStatusIcon = (status) => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('delivered')) return <CheckCircle className="w-6 h-6 text-green-600" />;
    if (lowerStatus.includes('denied') || lowerStatus.includes('not found')) return <XCircle className="w-6 h-6 text-red-600" />;
    return <Package className="w-6 h-6 text-blue-600" />;
  };

  const getStatusColor = (status) => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('delivered')) return 'bg-green-100 text-green-800 border-green-300';
    if (lowerStatus.includes('denied') || lowerStatus.includes('not found')) return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-blue-100 text-blue-800 border-blue-300';
  };

  const currentStatus = data.statusHistory[data.statusHistory.length - 1];

  return (
    <div 
      className="space-y-6"
      data-component-path="client/src/components/TrackingResult.jsx"
      data-component-line="1:1"
      data-component-id="client/src/components/TrackingResult.jsx:1:1"
    >
      {/* Header Card */}
      <div 
        className="bg-white rounded-lg shadow-lg p-6 md:p-8"
        data-component-path="client/src/components/TrackingResult.jsx"
        data-component-line="2:3"
        data-component-id="client/src/components/TrackingResult.jsx:2:3"
      >
        <div 
          className="flex items-start gap-4 mb-6"
          data-component-path="client/src/components/TrackingResult.jsx"
          data-component-line="3:5"
          data-component-id="client/src/components/TrackingResult.jsx:3:5"
        >
          <div 
            className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0"
            data-component-path="client/src/components/TrackingResult.jsx"
            data-component-line="4:7"
            data-component-id="client/src/components/TrackingResult.jsx:4:7"
          >
            {getStatusIcon(currentStatus.status)}
          </div>
          <div 
            className="flex-1"
            data-component-path="client/src/components/TrackingResult.jsx"
            data-component-line="5:7"
            data-component-id="client/src/components/TrackingResult.jsx:5:7"
          >
            <h2 
              className="text-2xl md:text-3xl font-bold text-gray-800 mb-2"
              data-component-path="client/src/components/TrackingResult.jsx"
              data-component-line="6:9"
              data-component-id="client/src/components/TrackingResult.jsx:6:9"
            >
              Tracking #{trackingNumber}
            </h2>
            <div 
              className={`inline-block px-4 py-2 rounded-lg border-2 font-semibold ${getStatusColor(currentStatus.status)}`}
              data-component-path="client/src/components/TrackingResult.jsx"
              data-component-line="7:9"
              data-component-id="client/src/components/TrackingResult.jsx:7:9"
            >
              {currentStatus.status}
            </div>
          </div>
        </div>

        {/* Shipment Details */}
        <div 
          className="grid md:grid-cols-2 gap-6 pt-6 border-t border-gray-200"
          data-component-path="client/src/components/TrackingResult.jsx"
          data-component-line="8:5"
          data-component-id="client/src/components/TrackingResult.jsx:8:5"
        >
          <div 
            className="flex items-start gap-3"
            data-component-path="client/src/components/TrackingResult.jsx"
            data-component-line="9:7"
            data-component-id="client/src/components/TrackingResult.jsx:9:7"
          >
            <MapPin 
              className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" 
              data-component-path="client/src/components/TrackingResult.jsx"
              data-component-line="10:9"
              data-component-id="client/src/components/TrackingResult.jsx:10:9"
            />
            <div data-component-path="client/src/components/TrackingResult.jsx" data-component-line="11:9" data-component-id="client/src/components/TrackingResult.jsx:11:9">
              <p 
                className="text-sm text-gray-500 mb-1"
                data-component-path="client/src/components/TrackingResult.jsx"
                data-component-line="12:11"
                data-component-id="client/src/components/TrackingResult.jsx:12:11"
              >
                From
              </p>
              <p 
                className="font-semibold text-gray-800"
                data-component-path="client/src/components/TrackingResult.jsx"
                data-component-line="13:11"
                data-component-id="client/src/components/TrackingResult.jsx:13:11"
              >
                {data.senderCity}
              </p>
            </div>
          </div>

          <div 
            className="flex items-start gap-3"
            data-component-path="client/src/components/TrackingResult.jsx"
            data-component-line="14:7"
            data-component-id="client/src/components/TrackingResult.jsx:14:7"
          >
            <MapPin 
              className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" 
              data-component-path="client/src/components/TrackingResult.jsx"
              data-component-line="15:9"
              data-component-id="client/src/components/TrackingResult.jsx:15:9"
            />
            <div data-component-path="client/src/components/TrackingResult.jsx" data-component-line="16:9" data-component-id="client/src/components/TrackingResult.jsx:16:9">
              <p 
                className="text-sm text-gray-500 mb-1"
                data-component-path="client/src/components/TrackingResult.jsx"
                data-component-line="17:11"
                data-component-id="client/src/components/TrackingResult.jsx:17:11"
              >
                To
              </p>
              <p 
                className="font-semibold text-gray-800"
                data-component-path="client/src/components/TrackingResult.jsx"
                data-component-line="18:11"
                data-component-id="client/src/components/TrackingResult.jsx:18:11"
              >
                {data.receiverCity}
              </p>
            </div>
          </div>

          <div 
            className="flex items-start gap-3"
            data-component-path="client/src/components/TrackingResult.jsx"
            data-component-line="19:7"
            data-component-id="client/src/components/TrackingResult.jsx:19:7"
          >
            <Package 
              className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" 
              data-component-path="client/src/components/TrackingResult.jsx"
              data-component-line="20:9"
              data-component-id="client/src/components/TrackingResult.jsx:20:9"
            />
            <div data-component-path="client/src/components/TrackingResult.jsx" data-component-line="21:9" data-component-id="client/src/components/TrackingResult.jsx:21:9">
              <p 
                className="text-sm text-gray-500 mb-1"
                data-component-path="client/src/components/TrackingResult.jsx"
                data-component-line="22:11"
                data-component-id="client/src/components/TrackingResult.jsx:22:11"
              >
                Service Type
              </p>
              <p 
                className="font-semibold text-gray-800"
                data-component-path="client/src/components/TrackingResult.jsx"
                data-component-line="23:11"
                data-component-id="client/src/components/TrackingResult.jsx:23:11"
              >
                {data.courierType}
              </p>
            </div>
          </div>

          {data.estimatedDelivery && (
            <div 
              className="flex items-start gap-3"
              data-component-path="client/src/components/TrackingResult.jsx"
              data-component-line="24:9"
              data-component-id="client/src/components/TrackingResult.jsx:24:9"
            >
              <Clock 
                className="w-5 h-5 text-orange-600 flex-shrink-0 mt-1" 
                data-component-path="client/src/components/TrackingResult.jsx"
                data-component-line="25:11"
                data-component-id="client/src/components/TrackingResult.jsx:25:11"
              />
              <div data-component-path="client/src/components/TrackingResult.jsx" data-component-line="26:11" data-component-id="client/src/components/TrackingResult.jsx:26:11">
                <p 
                  className="text-sm text-gray-500 mb-1"
                  data-component-path="client/src/components/TrackingResult.jsx"
                  data-component-line="27:13"
                  data-component-id="client/src/components/TrackingResult.jsx:27:13"
                >
                  Estimated Delivery
                </p>
                <p 
                  className="font-semibold text-gray-800"
                  data-component-path="client/src/components/TrackingResult.jsx"
                  data-component-line="28:13"
                  data-component-id="client/src/components/TrackingResult.jsx:28:13"
                >
                  {data.estimatedDelivery}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Timeline */}
      <div 
        className="bg-white rounded-lg shadow-lg p-6 md:p-8"
        data-component-path="client/src/components/TrackingResult.jsx"
        data-component-line="29:3"
        data-component-id="client/src/components/TrackingResult.jsx:29:3"
      >
        <h3 
          className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"
          data-component-path="client/src/components/TrackingResult.jsx"
          data-component-line="30:5"
          data-component-id="client/src/components/TrackingResult.jsx:30:5"
        >
          <AlertCircle 
            className="w-6 h-6 text-blue-600" 
            data-component-path="client/src/components/TrackingResult.jsx"
            data-component-line="31:7"
            data-component-id="client/src/components/TrackingResult.jsx:31:7"
          />
          Shipment Timeline
        </h3>
        <StatusTimeline statusHistory={data.statusHistory} />
      </div>
    </div>
  );
};

export default TrackingResult;